/**
 * Pay compensation installment — requires proof upload + email preview before send.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { Close, Send, Email, AttachFile } from "@mui/icons-material";
import {
  previewCompensationInstallmentEmail,
  previewCompensationProviderFyiEmail,
  payCompensationInstallment,
} from "../services/compensationService.js";
import { formatMoney } from "../utils/formatMoney.js";
import { logError } from "../../../shared/utils/logger.js";

export default function CompensationPayInstallmentModal({
  open,
  onClose,
  jobId,
  plan,
  installment,
  processedBy,
  onPaid,
}) {
  const [reference, setReference] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [customerEmail, setCustomerEmail] = useState(null);
  const [emailFound, setEmailFound] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [providerEmailContent, setProviderEmailContent] = useState("");
  const [providerEmail, setProviderEmail] = useState(null);
  const [providerEmailFound, setProviderEmailFound] = useState(false);
  const [showProviderFyi, setShowProviderFyi] = useState(false);
  const [sendProviderFyi, setSendProviderFyi] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [providerPreviewLoading, setProviderPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const defaultReference =
    plan && installment ? `COMP-${plan.id}-${installment.sequence}` : "";

  const loadPreview = useCallback(async () => {
    if (!jobId || !plan?.id || !installment) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const ref = reference.trim() || defaultReference;
      const result = await previewCompensationInstallmentEmail(
        jobId,
        plan.id,
        installment.sequence,
        { reference: ref }
      );
      if (result.success) {
        setEmailContent(result.data.emailContent || "");
        setCustomerEmail(result.data.customerEmail || null);
        setEmailFound(!!result.data.emailFound);
      } else {
        setError(result.error || "Failed to load email preview");
      }
    } catch (err) {
      setError(err.message || "Failed to load email preview");
      logError("Compensation email preview", { error: err.message });
    } finally {
      setPreviewLoading(false);
    }
  }, [jobId, plan?.id, installment, reference, defaultReference]);

  const loadProviderPreview = useCallback(async () => {
    if (!jobId || !plan?.id || !installment || !plan.adjustmentId) {
      setShowProviderFyi(false);
      return;
    }
    setProviderPreviewLoading(true);
    try {
      const ref = reference.trim() || defaultReference;
      const result = await previewCompensationProviderFyiEmail(
        jobId,
        plan.id,
        installment.sequence,
        { reference: ref }
      );
      if (result.success && !result.skip && !result.data?.skip) {
        setShowProviderFyi(true);
        setProviderEmailContent(result.data.emailContent || "");
        setProviderEmail(result.data.providerEmail || null);
        setProviderEmailFound(!!result.data.providerEmail);
        setSendProviderFyi(!!result.data.providerEmail);
      } else {
        setShowProviderFyi(false);
      }
    } catch {
      setShowProviderFyi(false);
    } finally {
      setProviderPreviewLoading(false);
    }
  }, [jobId, plan, installment, reference, defaultReference]);

  useEffect(() => {
    if (!open || !plan || !installment) return;
    setReference(defaultReference);
    setProofFile(null);
    setProviderEmailContent("");
    setShowProviderFyi(false);
    setError(null);
  }, [open, plan?.id, installment?.sequence, defaultReference]);

  useEffect(() => {
    if (!open || !plan || !installment || !reference) return;
    const t = setTimeout(() => {
      loadPreview();
      loadProviderPreview();
    }, 400);
    return () => clearTimeout(t);
  }, [reference, loadPreview, loadProviderPreview]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setProofFile(file || null);
    setError(null);
  };

  const handleSend = async () => {
    setError(null);

    if (!proofFile) {
      setError("Upload proof of payment (PDF or image) before continuing.");
      return;
    }
    if (!emailContent.trim()) {
      setError("Email content is required. Review the preview below.");
      return;
    }
    if (!emailFound) {
      setError("Customer email is missing — cannot send confirmation.");
      return;
    }

    const formData = new FormData();
    formData.append("proofOfPayment", proofFile);
    formData.append("emailContent", emailContent.trim());
    formData.append("reference", reference.trim() || defaultReference);
    formData.append("processedBy", processedBy || "system");
    formData.append("method", "bank_transfer");
    if (showProviderFyi && sendProviderFyi && providerEmailContent.trim()) {
      formData.append("providerEmailContent", providerEmailContent.trim());
    }

    try {
      setLoading(true);
      const result = await payCompensationInstallment(
        jobId,
        plan.id,
        installment.sequence,
        formData
      );
      if (result.success) {
        onPaid?.(result);
        handleClose();
      } else {
        setError(result.error || "Failed to complete payment");
      }
    } catch (err) {
      setError(err.message || "Failed to complete payment");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setProofFile(null);
    setError(null);
    onClose?.();
  };

  if (!installment || !plan) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Pay customer — installment {installment.sequence}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          You must upload proof of payment and send the previewed email to the customer.
          The email includes a credit note plus your proof as attachments. The installment is
          only marked paid after the email sends successfully.
        </Alert>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Amount: <strong>{formatMoney(installment.amount)}</strong>
        </Typography>

        <TextField
          label="Payment reference (EFT ref)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          onBlur={loadPreview}
          fullWidth
          required
          sx={{ mb: 2 }}
          helperText="Changing the reference refreshes the email preview"
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Proof of payment (required)
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFile />}
            sx={{ textTransform: "none" }}
          >
            {proofFile ? "Change file" : "Upload proof"}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              onChange={handleFileChange}
            />
          </Button>
          {proofFile && (
            <Chip
              label={proofFile.name}
              onDelete={() => setProofFile(null)}
              sx={{ ml: 1 }}
              size="small"
            />
          )}
        </Box>

        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            bgcolor: emailFound ? "#e8f5e9" : "#ffebee",
            border: `1px solid ${emailFound ? "#4caf50" : "#f44336"}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Email color={emailFound ? "success" : "error"} />
            <Typography variant="body2" fontWeight="bold">
              Customer email
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {emailFound ? customerEmail : "No email on file — add before paying"}
          </Typography>
        </Box>

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Email preview (editable)
        </Typography>
        {previewLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TextField
            fullWidth
            multiline
            rows={14}
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            required
            helperText="Credit note PDF + proof of payment will be attached when you send"
          />
        )}

        {showProviderFyi && (
          <>
            <Divider sx={{ my: 3 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendProviderFyi}
                  onChange={(e) => setSendProviderFyi(e.target.checked)}
                  disabled={!providerEmailFound}
                />
              }
              label="Send informational email to service provider (optional)"
            />
            <Alert severity="info" sx={{ mb: 2 }}>
              Separate notice for the partner — explains customer payment and recovery position. No
              links or actions required from them.
            </Alert>
            {providerPreviewLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Provider: {providerEmailFound ? providerEmail : "No email on file"}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={providerEmailContent}
                  onChange={(e) => setProviderEmailContent(e.target.value)}
                  disabled={!sendProviderFyi}
                  helperText="Plain informational notice only"
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
          onClick={handleSend}
          disabled={
            loading ||
            previewLoading ||
            !proofFile ||
            !emailContent.trim() ||
            !emailFound
          }
          sx={{ textTransform: "none" }}
        >
          Send email & mark paid
        </Button>
      </DialogActions>
    </Dialog>
  );
}
